/**
 * CreateSessionInlineFormWrapper Component
 *
 * Inline form component for creating new sessions or breaks within a flow's slot list.
 * Supports both Session and Break slot types with different form schemas and behaviors.
 *
 * Key Features:
 * - Duration adjustment buttons with long-press support
 * - Auto-focus and auto-open Select dropdown for sessions
 * - Click-away handling with special cases for Select menus and other slot interactions
 * - Form validation and submission via socket events
 * - Responsive behavior for small screens (prevents overlay opening)
 *
 * Component Structure:
 * - Left: Timing section (time display, duration display, +/- buttons)
 * - Middle: Form fields (session type selector or break display, title input)
 * - Right: Action buttons (confirm, cancel)
 */

import { Box, Card, Divider, Stack, TextField, Typography, Button, useTheme, ClickAwayListener } from '@mui/material';
import { useState, useEffect, useRef, useCallback, useMemo, startTransition } from 'react';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { parse, format, addMinutes } from 'date-fns';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowDropDownOutlined from '@mui/icons-material/ArrowDropDownOutlined';
import ArrowDropUpOutlined from '@mui/icons-material/ArrowDropUpOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useBreakpointDown } from '@/hooks/use-breakpoint';
import PauseCircleOutlined from '@mui/icons-material/PauseCircleOutlined';
import { useTranslations } from '@/i18n/use-translations';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch, type FieldErrors, type Resolver } from 'react-hook-form';
import { useNotifications } from '@/hooks/use-notifications';
import { useFlowSyncTransport } from '@/state/mutations/flows/use-flow-sync-transport';
import { mergeServerFlowWithClientContext } from '@/state/mutations/flows/merge-server-flow-with-client-context';
import { arrayMove } from '@dnd-kit/sortable';
import { createSessionSchemaFactory } from '@/schemas/sessions/create-session.ts';
import { SESSION_TYPE } from '@/types/dtos/sessions/session-type.enum.ts';
import SessionTypeInfo, { BreakSlotTypeInfo } from '@/components/common/session-type-info';
import sessionTypeToIcon from '@/components/events/detail/tabs/sessions/helpers/session-type-to-icon.helper';
import { type SessionType } from '@/types/dtos/sessions/session-type.enum';
import { useCreateSessionInFlowMutation } from '@/state/mutations/flows/use-create-session-in-flow';
import type { CreateSessionInFlowMutationBody } from '@/state/mutations/flows/apply-flow-mutation.http';
import { FormControl, MenuItem, Select } from '@mui/material';
import { cloneElement, isValidElement, type ReactElement } from 'react';
import { useEventStore } from '@/state/stores/use-event-store';
import { useOfferedSessionTypes } from '@/hooks/use-offered-session-types';
import { getObjectId } from '@/helpers/object.id.helper';

/** Dev-only logs for debugging session create + flowSync (filter console by `[createSessionInline]`). */
const logCreateSessionInline = (message: string, data?: Record<string, unknown>) => {
  if (!import.meta.env.DEV) return;
  if (data !== undefined) {
    console.debug(`[createSessionInline] ${message}`, data);
  } else {
    console.debug(`[createSessionInline] ${message}`);
  }
};

function findCreatedBreakSlotForSelection(
  slots: FlowSlotDto[],
  opts: { title: string; clampedDurationMinutes: number; offsetMinutes: number; isUntimed: boolean },
): FlowSlotDto | undefined {
  return slots.find(
    (slot) =>
      slot.contentType === 'Break' &&
      (opts.isUntimed
        ? (slot.isUntimed ?? (slot.plannedOffsetMinutes === undefined && slot.computedOffsetMinutes === undefined))
        : slot.computedOffsetMinutes === opts.offsetMinutes) &&
      slot.computedDurationMinutes === opts.clampedDurationMinutes &&
      slot.title === opts.title,
  );
}

/** Select value when creating a break from the unified session/break picker (not a `SessionType`). */
const INLINE_BREAK_SELECT_VALUE = '__INLINE_BREAK__' as const;

/** Non-secure origins (http) and some runtimes lack `crypto.randomUUID`. */
const newOptimisticSlotId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `optimistic-${crypto.randomUUID()}`;
  }
  return `optimistic-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
};

// ============================================================================
// TYPE DEFINITIONS & SCHEMAS
// ============================================================================

// Form schema for break creation (simplified - no session type needed)
// For untimed slots, offsetMinutes is optional
const createBreakFormSchema = (isUntimed: boolean) =>
  z.object({
    title: z.string().min(1, 'Title is required'),
    durationMinutes: z.number().int().positive(),
    ...(isUntimed ? {} : { offsetMinutes: z.number().min(0) }),
  });

// Form schema for session creation
type SessionFormType = z.infer<ReturnType<typeof createSessionSchemaFactory>>;
type SessionInlineFormType = Omit<SessionFormType, 'plannedDurationMinutes'> & {
  durationMinutes: number;
  offsetMinutes?: number; // Optional for untimed slots
};

type BreakFormType = z.infer<ReturnType<typeof createBreakFormSchema>>;
/** Values for the inline create form (session picker may include synthetic break option). */
type CreateFlowInlineFormValues = BreakFormType & {
  type?: '' | typeof INLINE_BREAK_SELECT_VALUE | SessionType;
};

interface CreateSessionInlineFormWrapperProps {
  offset: number;
  /** Max minutes until the next constraint (fixed slot, flow end, or midnight). */
  durationMinutes: number;
  /** Initial planned duration when the form opens (must match parent `formDuration` for preview). */
  initialPlannedDurationMinutes?: number;
  onSuccess?: (newSlot?: FlowSlotDto) => void;
  onClose?: () => void;
  setSelectedSlot: (slot?: FlowSlotDto) => void;
  onSuppressOverlayOpen?: () => void;
  onFormDurationChange?: (duration: number) => void;
  slotType?: 'Session' | 'Break'; // Default to 'Break' for backward compatibility
  insertBeforeSlotId?: string; // Optional: if provided, insert before this slot instead of using offset comparison
  insertAfterSlotId?: string; // Optional: if provided, insert after this slot instead of using offset comparison
  isUntimed?: boolean; // If true, don't set offsets and hide time display
}

export default function CreateSessionInlineFormWrapper({
  offset,
  durationMinutes: maxDurationMinutes,
  initialPlannedDurationMinutes,
  onSuccess,
  onClose,
  setSelectedSlot,
  onSuppressOverlayOpen,
  onFormDurationChange,
  slotType = 'Break',
  insertBeforeSlotId,
  insertAfterSlotId,
  isUntimed = false,
}: CreateSessionInlineFormWrapperProps) {
  // ============================================================================
  // HOOKS & INITIAL SETUP
  // ============================================================================

  const t = useTranslations();
  const notification = useNotifications();
  const theme = useTheme();
  const isSmallScreen = useBreakpointDown(860); // Match isWideScreen threshold for overlay behavior
  const flow = useFlowStore((s) => s.flow);
  const event = useEventStore((s) => s.event);
  const eventOriginId = event?.origin ? getObjectId(event.origin) : event?.id;
  const { selectableSessionTypes } = useOfferedSessionTypes(eventOriginId);
  const setFlow = useFlowStore((s) => s.setFlow);
  const { mutateAsync: createSessionInFlowAsync, isPending: createSessionInFlowPending } =
    useCreateSessionInFlowMutation();
  const { emitFlowSync, canEmit, useHttp, isConnected } = useFlowSyncTransport();
  const flowStartOffset = flow?.slot?.startOffsetMinutes ?? 0;
  /** Opened via BREAK action: static break row (no session-type list). Session entry uses Select with Break + session types. */
  const openedAsBreakOnly = slotType === 'Break';

  // ============================================================================
  // DEVICE DETECTION & STATE MANAGEMENT
  // ============================================================================

  // Detect if device has touch capability (mobile/tablet) - for timing delays
  const isTouchDevice = useMemo(() => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  const openingPlannedMinutesRef = useRef(initialPlannedDurationMinutes ?? 30);
  // Default 30 min, or less when the parent caps the gap (e.g. before a fixed-start slot).
  // When maxDurationMinutes is 0, the form can still show planned duration vs 0 available.
  const [currentDuration, setCurrentDuration] = useState(() => initialPlannedDurationMinutes ?? 30);
  const [currentOffset] = useState(offset);

  // Form state
  const [addingPending, setAddingPending] = useState(false); // Loading state during submission
  const [selectOpen, setSelectOpen] = useState(false); // Session type Select dropdown open state

  // Refs for DOM elements and flags
  const titleInputRef = useRef<HTMLInputElement>(null); // Reference to title input for auto-focus
  const hasAutoOpenedRef = useRef(false); // Track if Select dropdown has auto-opened
  const wasManuallyClosedRef = useRef(false); // Track if Select was manually closed (prevents re-opening)
  const isSelectInteractingRef = useRef(false); // Flag to prevent click-away during Select interaction
  const selectFormControlRef = useRef<HTMLDivElement>(null);
  /** False after unmount; we close the form before emit completes so async handlers must not set local React state. */
  const mountedRef = useRef(true);

  // Controlled Select `open` can leave focus on the menu paper while MUI sets aria-hidden on close.
  const releaseSelectMenuFocus = useCallback(() => {
    const active = document.activeElement as HTMLElement | null;
    if (active?.closest('.MuiMenu-root, [role="listbox"]')) {
      active.blur();
    }
    const trigger = selectFormControlRef.current?.querySelector(
      '[role="combobox"], [role="button"][aria-haspopup="listbox"]',
    ) as HTMLElement | undefined;
    trigger?.focus({ preventScroll: true });
  }, []);

  const setSelectOpenSafely = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        releaseSelectMenuFocus();
        requestAnimationFrame(() => setSelectOpen(false));
        return;
      }
      setSelectOpen(true);
    },
    [releaseSelectMenuFocus],
  );
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Track if duration change is coming from button click (to prevent circular updates)
  const isUpdatingFromButtonRef = useRef(false);

  // Store latest callback in ref to avoid closure issues
  const onFormDurationChangeRef = useRef(onFormDurationChange);
  useEffect(() => {
    onFormDurationChangeRef.current = onFormDurationChange;
  }, [onFormDurationChange]);

  // ============================================================================
  // FORM SETUP & CONFIGURATION
  // ============================================================================

  const inlineFormResolver = useMemo(() => {
    const createSessionSchema = createSessionSchemaFactory(t);
    const sessionFormSchema = createSessionSchema.omit({ plannedDurationMinutes: true }).extend({
      durationMinutes: z.number().int().positive(),
      ...(isUntimed ? {} : { offsetMinutes: z.number().min(0) }),
    });
    const breakFormSchema = createBreakFormSchema(isUntimed);
    /** Keeps `type` on parsed values so `handleSubmit` can branch (plain break schema strips unknown keys). */
    const breakFormSchemaWithSelectMarker = breakFormSchema.extend({
      type: z.literal(INLINE_BREAK_SELECT_VALUE),
    });
    return async (values, context, options) => {
      const isBreak = slotType === 'Break' || values.type === INLINE_BREAK_SELECT_VALUE;
      if (isBreak) {
        const breakPayload = {
          type: INLINE_BREAK_SELECT_VALUE,
          title: values.title,
          durationMinutes: values.durationMinutes,
          ...(isUntimed ? {} : { offsetMinutes: values.offsetMinutes ?? 0 }),
        };
        return zodResolver(breakFormSchemaWithSelectMarker)(breakPayload, context, options as never);
      }
      const offsetMinutes = !isUntimed && typeof values.offsetMinutes === 'number' ? values.offsetMinutes : undefined;
      const sessionPayload: SessionInlineFormType = {
        type: values.type as SessionType,
        title: values.title,
        durationMinutes: values.durationMinutes,
        ...(isUntimed ? {} : { offsetMinutes: offsetMinutes ?? 0 }),
      };
      return zodResolver(sessionFormSchema)(sessionPayload, context, options as never);
    };
  }, [t, isUntimed, slotType]) as Resolver<CreateFlowInlineFormValues>;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    reset,
    getValues,
    trigger,
  } = useForm<CreateFlowInlineFormValues>({
    resolver: inlineFormResolver,
    mode: 'onChange', // Validate on change to keep isValid up-to-date for button state
    defaultValues: openedAsBreakOnly
      ? {
          title: t('flows.detail.slotList.actions.break.title'),
          durationMinutes: initialPlannedDurationMinutes ?? 30,
          ...(isUntimed ? {} : { offsetMinutes: currentOffset }),
        }
      : {
          type: '',
          title: '',
          durationMinutes: currentDuration,
          ...(isUntimed ? {} : { offsetMinutes: currentOffset }),
        },
  });

  const selectedType = useWatch({ control, name: 'type', defaultValue: '' });
  const effectiveCreatingBreak = openedAsBreakOnly || selectedType === INLINE_BREAK_SELECT_VALUE;

  const selectedTypeRef = useRef(selectedType);
  useEffect(() => {
    selectedTypeRef.current = selectedType;
  }, [selectedType]);

  // Ensure default break title is set when Break is chosen (so confirm works without editing).
  useEffect(() => {
    if (openedAsBreakOnly || selectedType !== INLINE_BREAK_SELECT_VALUE) return;
    const title = getValues('title');
    if (!title?.trim()) {
      setValue('title', t('flows.detail.slotList.actions.break.title'), {
        shouldValidate: true,
        shouldDirty: true,
      });
      void trigger();
    }
  }, [getValues, openedAsBreakOnly, selectedType, setValue, t, trigger]);

  // ============================================================================
  // FORM VALUE SYNCING & WATCHERS
  // ============================================================================

  // Sync form values when timing changes from timing section
  const lastDurationRef = useRef<number | undefined>(currentDuration);
  const isSyncingFromPropRef = useRef(false); // Prevent circular updates when syncing from prop

  // Sync duration from state to form field (when changed via buttons)
  useEffect(() => {
    if (currentDuration !== undefined && lastDurationRef.current !== currentDuration) {
      isSyncingFromPropRef.current = true;
      startTransition(() => {
        setValue('durationMinutes', currentDuration, {
          shouldDirty: false,
          shouldValidate: false,
          shouldTouch: false,
        });
      });
      lastDurationRef.current = currentDuration;
      requestAnimationFrame(() => {
        isSyncingFromPropRef.current = false;
      });
    }
  }, [currentDuration, setValue]);

  // Sync offset from prop to form field
  const lastOffsetRef = useRef<number | undefined>(currentOffset);
  useEffect(() => {
    if (currentOffset !== undefined && lastOffsetRef.current !== currentOffset) {
      startTransition(() => {
        setValue('offsetMinutes', currentOffset, {
          shouldDirty: false,
          shouldValidate: false,
          shouldTouch: false,
        });
      });
      lastOffsetRef.current = currentOffset;
    }
  }, [currentOffset, setValue]);

  // Watch durationMinutes and notify parent when it changes (but not when syncing from currentDuration prop)
  const durationMinutes = useWatch({ control, name: 'durationMinutes', defaultValue: currentDuration });
  const sessionErrors = !effectiveCreatingBreak ? (errors as FieldErrors<SessionInlineFormType>) : undefined;
  useEffect(() => {
    if (durationMinutes !== undefined && !isSyncingFromPropRef.current) {
      // Use setTimeout to defer the call and avoid setState during render
      const timeoutId = setTimeout(() => {
        // durationMinutes is a number in this form, not a string
        const clampedMinutes = Math.max(1, durationMinutes); // Ensure minimum of 1 minute

        // Only update local state if change didn't come from button click
        if (!isUpdatingFromButtonRef.current) {
          setCurrentDuration(clampedMinutes);
          // Update form field if duration was clamped
          if (clampedMinutes !== durationMinutes) {
            setValue('durationMinutes', clampedMinutes, {
              shouldDirty: false,
              shouldValidate: false,
              shouldTouch: false,
            });
          }
        }
        // Always notify parent (List component) about duration change (use clamped value)
        onFormDurationChangeRef.current?.(clampedMinutes);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [durationMinutes, setValue]);

  // ============================================================================
  // UI BEHAVIOR EFFECTS (Auto-open Select, Auto-focus)
  // ============================================================================

  // Automatically open the Select dropdown when the form is mounted (only for sessions)
  useEffect(() => {
    if (!openedAsBreakOnly && !hasAutoOpenedRef.current && !wasManuallyClosedRef.current && !selectedType) {
      const timer = setTimeout(() => {
        if (!wasManuallyClosedRef.current && !selectedType) {
          setSelectOpen(true);
          hasAutoOpenedRef.current = true;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedType, openedAsBreakOnly]);

  // Auto-focus title input when type is selected (for sessions) or when form is mounted (for breaks)
  useEffect(() => {
    const shouldFocus = effectiveCreatingBreak || selectedType;
    if (shouldFocus && titleInputRef.current) {
      // Use touch device detection for timing (virtual keyboard needs more time)
      const delay = isTouchDevice ? 400 : 150;

      const timer = setTimeout(() => {
        if (titleInputRef.current) {
          const inputElement = titleInputRef.current.querySelector('input') as HTMLInputElement;
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          } else {
            titleInputRef.current.focus();
            titleInputRef.current.select();
          }
        }
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [selectedType, isTouchDevice, effectiveCreatingBreak]);

  // ============================================================================
  // FORM SUBMISSION HANDLER
  // ============================================================================
  const applyOptimisticFlowUpdate = useCallback(
    (slots: FlowSlotDto[]) => {
      if (!flow) return;
      const safeSlots = slots.filter((slot): slot is FlowSlotDto => !!slot && typeof slot.id === 'string');
      setFlow({
        ...flow,
        slots: safeSlots,
        version: flow.version + 1,
      });
    },
    [flow, setFlow],
  );

  // Form submission handler - create session or break slot
  // Handles both Break and Session creation with different logic paths
  const onSubmit = async (data: CreateFlowInlineFormValues) => {
    const creatingBreak =
      slotType === 'Break' ||
      data.type === INLINE_BREAK_SELECT_VALUE ||
      selectedTypeRef.current === INLINE_BREAK_SELECT_VALUE;

    if (!flow?.id) {
      notification.notifyError(
        creatingBreak
          ? t('flows.detail.slotList.actions.break.notification.createError')
          : t('session.notification.createError'),
      );
      return;
    }

    setAddingPending(true);

    logCreateSessionInline('onSubmit start', {
      slotType: creatingBreak ? 'Break' : 'Session',
      flowId: flow.id,
      flowVersion: flow.version,
      flowMode: flow.mode,
      flowParentType: flow.parentType,
      canEmit,
      useHttp,
      socketConnected: isConnected,
    });

    // Ensure duration is an integer (backend requirement)
    const plannedDurationMinutes = Math.round(data.durationMinutes);
    const clampedDurationMinutes = Math.min(plannedDurationMinutes, maxDurationMinutes);

    try {
      if (creatingBreak) {
        // ========================================================================
        // BREAK CREATION PATH
        // ========================================================================

        // Create break slot directly (no session creation needed)
        const breakTitle = (data as BreakFormType).title || t('flows.detail.slotList.actions.break.title');

        const tempSlotId = newOptimisticSlotId();
        const newSlots = [
          ...(flow.slots ?? []),
          {
            id: tempSlotId,
            title: breakTitle,
            contentType: 'Break' as const,
            content: undefined,
            plannedDurationMinutes: plannedDurationMinutes,
            isUntimed: isUntimed,
            // For untimed slots, don't set computedOffsetMinutes or plannedOffsetMinutes
            ...(isUntimed
              ? {}
              : {
                  computedDurationMinutes: clampedDurationMinutes,
                  computedOffsetMinutes: (data as BreakFormType).offsetMinutes ?? 0,
                  // Don't set plannedOffsetMinutes - breaks should not have fixed start-time by default
                }),
          },
        ];

        // Find insertion index based on offset or slot ID (for positioning)
        // For untimed slots, append to end of untimed slots (or end of all slots if no untimed slots exist)
        let insertionIndex: number;

        if (isUntimed) {
          // For untimed slots, find the position after all untimed slots
          const untimedSlotsCount = (flow?.slots ?? []).filter(
            (slot) =>
              slot.isUntimed ?? (slot.plannedOffsetMinutes === undefined && slot.computedOffsetMinutes === undefined),
          ).length;
          insertionIndex = (flow?.slots?.length ?? 0) - untimedSlotsCount;
        } else {
          // Ensures breaks are inserted in chronological order
          const plannedOffsetMinutes = typeof data.offsetMinutes === 'number' ? data.offsetMinutes : 0;

          if (insertBeforeSlotId) {
            // Insert before a specific slot
            const targetIndex = flow?.slots?.findIndex((slot) => slot.id === insertBeforeSlotId);
            insertionIndex = targetIndex !== undefined && targetIndex !== -1 ? targetIndex : (flow?.slots?.length ?? 0);
          } else if (insertAfterSlotId) {
            // Insert after a specific slot
            const targetIndex = flow?.slots?.findIndex((slot) => slot.id === insertAfterSlotId);
            insertionIndex =
              targetIndex !== undefined && targetIndex !== -1 ? targetIndex + 1 : (flow?.slots?.length ?? 0);
          } else {
            // Fallback to offset-based insertion
            insertionIndex =
              flow?.slots?.findIndex((slot) => (slot.computedOffsetMinutes ?? 0) > plannedOffsetMinutes) ?? -1;
            if (insertionIndex === -1) {
              insertionIndex = flow?.slots?.length ?? 0;
            }
          }
        }
        const swappedSlots = arrayMove(newSlots, newSlots.length - 1, insertionIndex);
        const normalizedSlots = swappedSlots.map((slot) => {
          const normalized = {
            ...slot,
            content:
              slot.contentType === 'Gap'
                ? undefined
                : typeof slot.content === 'object'
                  ? slot.content?.id
                  : slot.content,
          };
          if (slot.id === tempSlotId) {
            delete (normalized as { id?: string }).id;
          }
          return normalized;
        });

        // Extract startTime and endTime from slot if present (matching syncFlow pattern)
        // This preserves the fixed end-time (durationPlannedMinutes) when adding slots
        let startTime: string | undefined;
        let endTime: string | null | undefined;

        if (flow.slot?.startOffsetMinutes !== undefined) {
          startTime = minuteOffsetToTime(flow.slot.startOffsetMinutes);

          // Use flow.durationPlannedMinutes as primary source (flow is source of truth)
          const flowDurationPlannedMinutes = flow.durationPlannedMinutes;
          // Check if durationPlannedMinutes is explicitly set and greater than 0
          if (flowDurationPlannedMinutes !== undefined && flowDurationPlannedMinutes > 0) {
            // Fixed end-time is set
            endTime = minuteOffsetToTime(flow.slot.startOffsetMinutes + flowDurationPlannedMinutes);
          } else {
            // Fixed end-time is being removed (undefined, null, or <= 0) - explicitly set to null to clear it
            endTime = null;
          }
        }

        // Ensure flow has required fields for syncFlowSchema
        // Follow the same pattern as syncFlow: convert slot to startTime/endTime strings
        const flowToSync = {
          id: flow.id,
          version: flow.version,
          parentType: flow.parentType,
          title: flow.title,
          description: flow.description,
          slots: normalizedSlots,
          slot: undefined, // Remove slot object - backend expects startTime/endTime strings
          startDate: undefined,
          ...(startTime && { startTime }),
          // Always include endTime when startTime is present, even if null (to clear it)
          ...(startTime !== undefined && { endTime: endTime ?? null }),
        };

        if (!canEmit) {
          console.warn('[createSessionInline] flow sync not sent (break): not connected', {
            canEmit,
            useHttp,
            socketConnected: isConnected,
          });
          notification.notifyError(t('flows.detail.slotList.actions.break.notification.createError'));
          setAddingPending(false);
          return;
        }

        logCreateSessionInline('emitting flowSync (break)', {
          flowId: flowToSync.id,
          version: flowToSync.version,
          slotCount: flowToSync.slots?.length,
        });

        const previousFlowForBreak = useFlowStore.getState().flow;
        applyOptimisticFlowUpdate(swappedSlots as FlowSlotDto[]);
        if (!isSmallScreen) {
          const breakForm = data as BreakFormType;
          const rawOffset = isUntimed ? undefined : (breakForm as { offsetMinutes?: unknown }).offsetMinutes;
          const breakOffsetMinutes = typeof rawOffset === 'number' ? rawOffset : 0;
          const breakSlot = findCreatedBreakSlotForSelection(swappedSlots as FlowSlotDto[], {
            title: breakTitle,
            clampedDurationMinutes,
            offsetMinutes: breakOffsetMinutes,
            isUntimed,
          });
          if (breakSlot) {
            setSelectedSlot(breakSlot);
          }
        }
        setAddingPending(false);
        onClose?.();

        const breakIdempotencyKey =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : undefined;
        const breakResult = await emitFlowSync(
          { flow: flowToSync as Record<string, unknown> },
          breakIdempotencyKey ? { idempotencyKey: breakIdempotencyKey } : undefined,
        );
        logCreateSessionInline('flowSync ack (break)', { ok: breakResult.ok });
        if (breakResult.ok) {
          if (breakResult.flow) {
            setFlow(mergeServerFlowWithClientContext(breakResult.flow, previousFlowForBreak ?? undefined));
          }
          notification.notifySuccess(
            t('flows.detail.slotList.actions.break.notification.createSuccess', { title: breakTitle }),
          );

          if (isSmallScreen) {
            setSelectedSlot(undefined);
          }

          if (onSuppressOverlayOpen) {
            onSuppressOverlayOpen();
            if (isSmallScreen) {
              setTimeout(() => onSuppressOverlayOpen?.(), 100);
              setTimeout(() => onSuppressOverlayOpen?.(), 300);
              setTimeout(() => onSuppressOverlayOpen?.(), 600);
            }
          }

          setTimeout(() => {
            const updatedFlow = useFlowStore.getState().flow;
            if (updatedFlow?.slots) {
              const newSlot = updatedFlow.slots.find(
                (slot) =>
                  slot.contentType === 'Break' &&
                  (isUntimed
                    ? (slot.isUntimed ??
                      (slot.plannedOffsetMinutes === undefined && slot.computedOffsetMinutes === undefined))
                    : slot.computedOffsetMinutes === ((data as BreakFormType).offsetMinutes ?? 0)) &&
                  slot.computedDurationMinutes === clampedDurationMinutes,
              );

              if (newSlot) {
                requestAnimationFrame(() => {
                  const selectors = [`[data-slot-id="${newSlot.id}"]`, `#slot-${newSlot.id}`];

                  const el = selectors
                    .map((sel) => document.querySelector(sel))
                    .find((node): node is Element => !!node);

                  if (el && 'scrollIntoView' in el) {
                    (el as Element).scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }

                  setSelectedSlot(newSlot);

                  if (isSmallScreen) {
                    onSuccess?.();
                  } else {
                    onSuccess?.(newSlot);
                  }
                });
              } else {
                onSuccess?.();
              }
            } else {
              onSuccess?.();
            }
          }, 300);
        } else {
          setFlow(previousFlowForBreak ?? undefined);
          console.error('Failed to create break slot: flow sync rejected');
          notification.notifyError(t('flows.detail.slotList.actions.break.notification.createError'));
        }
        if (mountedRef.current) {
          setAddingPending(false);
        }
      } else {
        // ========================================================================
        // SESSION CREATION PATH
        // ========================================================================

        // Create session (original logic)
        const sessionData = data as SessionInlineFormType;

        if (flow.parentType === 'Event') {
          if (!flow.id || flow.version == null) {
            notification.notifyError(t('session.notification.createError'));
            setAddingPending(false);
            return;
          }

          let startTime: string | undefined;
          let endTime: string | null | undefined;
          if (flow.slot?.startOffsetMinutes !== undefined) {
            startTime = minuteOffsetToTime(flow.slot.startOffsetMinutes);
            const flowDurationPlannedMinutes = flow.durationPlannedMinutes;
            if (flowDurationPlannedMinutes !== undefined && flowDurationPlannedMinutes > 0) {
              endTime = minuteOffsetToTime(flow.slot.startOffsetMinutes + flowDurationPlannedMinutes);
            } else {
              endTime = null;
            }
          }

          const mutationBody: CreateSessionInFlowMutationBody = {
            type: 'createSessionInFlow',
            baseVersion: flow.version,
            payload: {
              session: {
                title: sessionData.title,
                type: sessionData.type as SessionType,
                plannedDurationMinutes,
              },
              slot: {
                plannedDurationMinutes,
                isUntimed,
                ...(insertBeforeSlotId ? { insertBeforeSlotId } : {}),
                ...(insertAfterSlotId ? { insertAfterSlotId } : {}),
                ...(isUntimed
                  ? {}
                  : {
                      computedDurationMinutes: clampedDurationMinutes,
                      computedOffsetMinutes: sessionData.offsetMinutes ?? 0,
                    }),
              },
              ...(startTime ? { startTime } : {}),
              ...(startTime !== undefined ? { endTime: endTime ?? null } : {}),
              ...(flow.slot?.id ? { eventSlotId: flow.slot.id } : {}),
              ...(flow.slot?.day?.id ? { eventDayId: flow.slot.day.id } : {}),
            },
          };

          logCreateSessionInline('POST createSessionInFlow', {
            flowId: flow.id,
            baseVersion: flow.version,
          });

          const previousFlowForSession = useFlowStore.getState().flow;
          const previousSessionIds = new Set(
            (previousFlowForSession?.slots ?? [])
              .filter((slot) => slot.contentType === 'Session' && slot.content && typeof slot.content === 'object')
              .map((slot) => (slot.content as { id: string }).id),
          );

          setAddingPending(false);
          onClose?.();

          const sessionIdempotencyKey =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : undefined;

          try {
            const updatedFlow = await createSessionInFlowAsync({
              flowId: flow.id,
              body: mutationBody,
              idempotencyKey: sessionIdempotencyKey,
            });

            setFlow(mergeServerFlowWithClientContext(updatedFlow, previousFlowForSession ?? undefined));

            const newSlot = updatedFlow.slots?.find((slot) => {
              if (slot.contentType !== 'Session' || !slot.content || typeof slot.content !== 'object') {
                return false;
              }
              return !previousSessionIds.has((slot.content as { id: string }).id);
            });
            const newSessionId =
              newSlot?.content && typeof newSlot.content === 'object'
                ? (newSlot.content as { id: string }).id
                : undefined;

            logCreateSessionInline('createSessionInFlow succeeded', {
              flowId: flow.id,
              sessionId: newSessionId,
              slotId: newSlot?.id,
            });

            notification.notifySuccess(t('session.notification.createSuccess', { title: sessionData.title }));

            if (isSmallScreen) {
              setSelectedSlot(undefined);
            }

            if (onSuppressOverlayOpen) {
              onSuppressOverlayOpen();
              if (isSmallScreen) {
                setTimeout(() => onSuppressOverlayOpen?.(), 100);
                setTimeout(() => onSuppressOverlayOpen?.(), 300);
                setTimeout(() => onSuppressOverlayOpen?.(), 600);
              }
            }

            setTimeout(() => {
              if (newSlot && newSessionId) {
                requestAnimationFrame(() => {
                  const selectors = [
                    `[data-slot-id="${newSlot.id}"]`,
                    `[data-flow-slot-id="${newSessionId}"]`,
                    `#flow-slot-${newSessionId}`,
                    `#slot-${newSlot.id}`,
                  ];

                  const el = selectors
                    .map((sel) => document.querySelector(sel))
                    .find((node): node is Element => !!node);

                  if (el && 'scrollIntoView' in el) {
                    (el as Element).scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }

                  setSelectedSlot(newSlot);

                  if (isSmallScreen) {
                    onSuccess?.();
                  } else {
                    onSuccess?.(newSlot);
                  }
                });
              } else {
                onSuccess?.();
              }
            }, 300);
          } catch (error) {
            console.error('[createSessionInline] createSessionInFlow failed', error);
            notification.notifyError(t('session.notification.createError'));
          }

          if (mountedRef.current) {
            setAddingPending(false);
          }
        } else {
          logCreateSessionInline('session path skipped: flow.parentType is not Event', {
            parentType: flow.parentType,
            flowId: flow.id,
          });
        }
      }
    } catch (error) {
      console.error('[createSessionInline] onSubmit threw', error);
      const notifyAsBreak = slotType === 'Break' || selectedTypeRef.current === INLINE_BREAK_SELECT_VALUE;
      notification.notifyError(
        notifyAsBreak
          ? t('flows.detail.slotList.actions.break.notification.createError')
          : t('session.notification.createError'),
      );
      if (mountedRef.current) {
        setAddingPending(false);
      }
    }
  };

  // Convert duration to HH:mm format for OffsetButtons - recalculate when currentDuration changes
  const durationAsTime = useMemo(
    () => format(addMinutes(parse('00:00', 'HH:mm', new Date()), currentDuration), 'HH:mm'),
    [currentDuration],
  );

  // Handle duration change from OffsetButtons
  const handleDurationChange = useCallback((newDurationTime: string) => {
    const date = parse(newDurationTime, 'HH:mm', new Date());
    const minutes = Math.max(1, date.getHours() * 60 + date.getMinutes()); // Ensure minimum of 1 minute
    isUpdatingFromButtonRef.current = true;
    setCurrentDuration(minutes);
    // Notify parent after state update completes (defer to avoid setState during render)
    setTimeout(() => {
      onFormDurationChangeRef.current?.(minutes);
      // Reset flag after notifying parent
      requestAnimationFrame(() => {
        isUpdatingFromButtonRef.current = false;
      });
    }, 0);
  }, []);

  // ============================================================================
  // LONG-PRESS SUPPORT FOR DURATION BUTTONS
  // ============================================================================

  // Refs for managing long-press behavior (continuous increment/decrement)
  const longPressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null); // Interval for continuous updates
  const longPressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Timeout before starting interval
  const isLongPressingRef = useRef(false); // Flag indicating if long-press is active
  const pendingTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set()); // Track pending timeouts for cleanup
  const touchStartTimeRef = useRef<number>(0); // Track when touch started (to differentiate tap vs long-press)
  const activeTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Track active timeout ID for race condition prevention
  const removeGlobalReleaseListenersRef = useRef<(() => void) | null>(null); // Cleanup function for global event listeners
  // End long-press and clean up all timers/listeners (defined first - used by handleLongPressStart)
  const handleLongPressEnd = useCallback(() => {
    const currentTimeoutId = longPressTimeoutRef.current;

    if (removeGlobalReleaseListenersRef.current) {
      removeGlobalReleaseListenersRef.current();
      removeGlobalReleaseListenersRef.current = null;
    }

    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
      if (activeTimeoutIdRef.current === currentTimeoutId) {
        activeTimeoutIdRef.current = null;
      }
    }

    if (longPressIntervalRef.current) {
      clearInterval(longPressIntervalRef.current);
      longPressIntervalRef.current = null;
    }

    pendingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    pendingTimeoutsRef.current.clear();

    isLongPressingRef.current = false;
  }, []);

  // Start long-press detection and continuous updates
  const handleLongPressStart = useCallback(
    (minutes: number) => {
      // CRITICAL: Always clear existing timeout/interval FIRST to prevent multiple intervals
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
        longPressTimeoutRef.current = null;
        activeTimeoutIdRef.current = null;
      }
      if (longPressIntervalRef.current) {
        clearInterval(longPressIntervalRef.current);
        longPressIntervalRef.current = null;
      }

      // Remove any previously registered global release listeners
      if (removeGlobalReleaseListenersRef.current) {
        removeGlobalReleaseListenersRef.current();
        removeGlobalReleaseListenersRef.current = null;
      }

      // Clear all pending timeouts from previous intervals
      pendingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      pendingTimeoutsRef.current.clear();

      // Track touch start time to differentiate short tap from long press
      touchStartTimeRef.current = Date.now();
      isLongPressingRef.current = false;

      // Only register global "release" listeners on touch devices (mobile/tablet)
      // On desktop, rely on button's own mouse event handlers
      // This handles cases where onTouchEnd doesn't fire on the button itself on mobile
      if (isTouchDevice) {
        const onRelease = () => handleLongPressEnd();
        const onVisibilityChange = () => {
          if (document.visibilityState !== 'visible') handleLongPressEnd();
        };

        window.addEventListener('touchend', onRelease, { capture: true, passive: true });
        window.addEventListener('touchcancel', onRelease, { capture: true, passive: true });
        window.addEventListener('pointerup', onRelease, { capture: true });
        window.addEventListener('pointercancel', onRelease, { capture: true });
        window.addEventListener('blur', onRelease, { capture: true });
        document.addEventListener('visibilitychange', onVisibilityChange, { capture: true });

        removeGlobalReleaseListenersRef.current = () => {
          const options: EventListenerOptions = { capture: true };
          window.removeEventListener('touchend', onRelease, options);
          window.removeEventListener('touchcancel', onRelease, options);
          window.removeEventListener('pointerup', onRelease, options);
          window.removeEventListener('pointercancel', onRelease, options);
          window.removeEventListener('blur', onRelease, options);
          document.removeEventListener('visibilitychange', onVisibilityChange, options);
        };
      } else {
        // On desktop, don't register global listeners - rely on button's own mouse handlers
        // This prevents interference with normal mouse event flow
        removeGlobalReleaseListenersRef.current = () => {
          // No-op on desktop
        };
      }

      // Set timeout for long press detection (500ms)
      const timeoutId = setTimeout(() => {
        // Double-check: Only start interval if this timeout ID matches the active one
        // This prevents race conditions where cleanup happens after timeout is queued
        if (activeTimeoutIdRef.current === timeoutId && longPressTimeoutRef.current === timeoutId) {
          isLongPressingRef.current = true;

          // Start interval for continuous increment/decrement
          const intervalId = setInterval(() => {
            // Defensive check: Only continue if interval is still active
            if (longPressIntervalRef.current !== intervalId) {
              clearInterval(intervalId);
              return;
            }

            setCurrentDuration((prev) => {
              const newDuration = Math.max(1, prev + minutes);
              // Use ref to get latest callback (avoids closure issues)
              const timeoutId = setTimeout(() => {
                // Only proceed if interval is still active
                if (longPressIntervalRef.current === intervalId) {
                  isUpdatingFromButtonRef.current = true;
                  onFormDurationChangeRef.current?.(newDuration);
                  requestAnimationFrame(() => {
                    isUpdatingFromButtonRef.current = false;
                  });
                }
                pendingTimeoutsRef.current.delete(timeoutId);
              }, 0);
              pendingTimeoutsRef.current.add(timeoutId);
              return newDuration;
            });
          }, 100); // Update every 100ms

          longPressIntervalRef.current = intervalId;
        }
      }, 500);

      longPressTimeoutRef.current = timeoutId;
      activeTimeoutIdRef.current = timeoutId;
    },
    [isTouchDevice, handleLongPressEnd],
  );

  // Cleanup on unmount - ensure all timers and listeners are cleared
  useEffect(() => {
    return () => {
      // Remove global release listeners
      if (removeGlobalReleaseListenersRef.current) {
        removeGlobalReleaseListenersRef.current();
        removeGlobalReleaseListenersRef.current = null;
      }

      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
      if (longPressIntervalRef.current) {
        clearInterval(longPressIntervalRef.current);
      }
      pendingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      pendingTimeoutsRef.current.clear();
    };
  }, []);

  // Handle cancel/close with form reset
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- form reset dependencies
  const handleCancel = useCallback(() => {
    // Reset form to initial values
    if (slotType === 'Break') {
      const reopenMinutes = openingPlannedMinutesRef.current;
      reset({
        title: t('flows.detail.slotList.actions.break.title'),
        durationMinutes: reopenMinutes,
        ...(isUntimed ? {} : { offsetMinutes: offset }),
      });
      setCurrentDuration(reopenMinutes);
    } else {
      reset({
        type: '',
        title: '',
        durationMinutes: currentDuration,
        ...(isUntimed ? {} : { offsetMinutes: offset }),
      });
      setSelectOpenSafely(false);
      wasManuallyClosedRef.current = false;
      hasAutoOpenedRef.current = false;
    }
    onClose?.();
  }, [reset, offset, onClose, t, slotType, currentDuration, setSelectOpenSafely]);

  // Handle click away - close form and reset values
  // Complex logic to handle various edge cases:
  // - Select dropdown interactions (menu, component, portal rendering)
  // - End-timing section clicks (should close form before opening end-timing form)
  // - Other slot timing section clicks (should close form before opening slot-timing form)
  // - Touch event delays for mobile devices
  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      // On mobile, touch events need more time to propagate and execute
      // The ClickAwayListener fires on mouseDown/touchStart, but we need to wait
      // for the click/tap to complete before closing
      const target = event.target as HTMLElement;
      const isTouchEvent = 'touches' in event || 'changedTouches' in event;

      // If Select is being interacted with, don't close the form
      if (isSelectInteractingRef.current) {
        return;
      }

      // If Select dropdown is open, don't close the form (allow user to interact with menu)
      if (selectOpen) {
        return;
      }

      // Check if clicking on Select dropdown menu (rendered in portal)
      // The menu might be rendered outside the form, so check globally first
      const clickedSelectMenu = target.closest('[role="listbox"]');
      if (clickedSelectMenu) {
        return;
      }

      // Also check for the Paper component that contains the menu
      const clickedMenuPaper = target.closest('.MuiPaper-root[role="presentation"]');
      if (clickedMenuPaper && clickedMenuPaper.querySelector('[role="listbox"]')) {
        return;
      }

      // Check if clicking on Select component itself (even when closed)
      // Check within the form element to ensure it's our Select
      const formElement = target.closest('[data-form-type="create"]');
      if (formElement) {
        const clickedSelect = target.closest(
          '.MuiSelect-root, .MuiSelect-select, [role="button"][aria-haspopup="listbox"], .MuiFormControl-root',
        );
        if (clickedSelect && formElement.contains(clickedSelect)) {
          return;
        }
      }

      // Check if clicking on the end-timing section (to open end-timing form)
      // Use the same detection logic as inline-slot-timing-form
      const isClickingEndTiming =
        !!target.closest('[data-end-timing-form]') ||
        (!!target.closest('[data-testid="flow-slot"]') && !!target.closest('[data-start-end-item]')) ||
        !!target.closest('[data-start-end-item]');

      // If clicking on end-timing section, close immediately with shorter delay
      // This ensures the create form closes before the end-timing form opens
      if (isClickingEndTiming) {
        const delay = isTouchEvent ? 50 : 0;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              handleCancel();
            }, delay);
          });
        });
        return;
      }

      // Check if clicking on another slot's timing section (to open its form)
      // Look for data-slot-id attribute or slot card elements
      const clickedSlotCard = target.closest('[data-slot-id]');
      const clickedSlotId = clickedSlotCard?.getAttribute('data-slot-id');

      // If clicking on another slot's timing section, close immediately with shorter delay
      // This ensures the create form closes before the slot-timing form opens
      if (clickedSlotId) {
        // Check if clicking on the timing section (first Stack div in the Card)
        // The timing section is the first child Stack in the Card
        const cardElement = clickedSlotCard as HTMLElement;
        if (cardElement) {
          const firstStack = cardElement.querySelector('div:first-child');
          if (firstStack && (firstStack.contains(target) || firstStack === target)) {
            // Clicking on another slot's timing section - close immediately with shorter delay
            const delay = isTouchEvent ? 50 : 0;
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setTimeout(() => {
                  handleCancel();
                }, delay);
              });
            });
            return;
          }
        }
        // If clicking on slot card (not timing section), fall through to close handler
      }

      // For all outside clicks (including clicking on slot cards), close the form
      const delay = isTouchEvent ? 100 : 0;

      // Use requestAnimationFrame twice to ensure the event loop processes the click
      // This allows the click event to fire and execute before we close the form
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Additional delay for touch events to ensure tap completes
          setTimeout(() => {
            handleCancel();
          }, delay);
        });
      });
    },
    [handleCancel, selectOpen],
  );

  // Handle Escape key to close form
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [handleCancel]);

  // Enhanced OffsetButtons usage - component is now imported from common

  // Calculate time display
  const time = isUntimed ? '' : minuteOffsetToTime(flowStartOffset + currentOffset);

  // Calculate duration display - show available/planned format when:
  // 1. No available time (availableDuration === 0) - show "0/planned min"
  // 2. Planned > available (plannedDuration > availableDuration > 0) - show "available/planned min"
  // Hard check: use strict comparison and ensure both values are valid numbers to prevent flickering
  const plannedDuration = currentDuration;
  const availableDuration = maxDurationMinutes;
  const hasNoAvailableTime =
    typeof availableDuration === 'number' &&
    availableDuration === 0 &&
    typeof plannedDuration === 'number' &&
    plannedDuration > 0;
  const isShortened =
    typeof plannedDuration === 'number' &&
    typeof availableDuration === 'number' &&
    availableDuration > 0 &&
    plannedDuration > availableDuration;
  const showAvailablePlannedFormat = hasNoAvailableTime || isShortened;
  const hasTwoLineDuration = showAvailablePlannedFormat;
  const duration = showAvailablePlannedFormat
    ? `${Math.max(0, availableDuration)}/${plannedDuration}`
    : `${plannedDuration} min`;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Card
        elevation={0}
        sx={{
          cursor: 'default',
          bgcolor: 'slot.normal.selected',
          outlineWidth: '2px',
          outlineColor: showAvailablePlannedFormat ? 'warning.main' : 'secondary.main',
          outlineStyle: 'solid',
          overflow: 'hidden',
          height: 'auto', // Taller than standard 48px to accommodate timing section + form fields
          minHeight: '64px', // Minimum height for timing section + form fields
          boxShadow: showAvailablePlannedFormat ? `0px 0px 8px ${theme.palette.warning.main} inset` : undefined,
        }}
      >
        <Stack direction="row" alignItems="stretch" height="100%">
          {/* ====================================================================
              LEFT SECTION: Timing Display & Duration Controls
              ==================================================================== */}
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              minWidth: '75px',
              flexShrink: 0,
              height: '100%',
              minHeight: '32px',
              cursor: 'default',
              py: 0.5,
            }}
          >
            <Stack direction="column" alignItems="center" justifyContent="center" spacing={0.5} gap={1}>
              <Stack direction="column" alignItems="center" justifyContent="center" spacing={0} gap={0}>
                {/* Time display - hide for untimed slots */}
                {!isUntimed && (
                  <Typography
                    variant="slotStartTime"
                    color="text.secondary"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}
                  >
                    {time}
                  </Typography>
                )}

                {/* Duration display */}
                {duration && (
                  <Stack direction="column" alignItems="center" spacing={0}>
                    <Typography
                      variant="slotDuration"
                      color={showAvailablePlannedFormat ? 'warning' : 'text.secondary'}
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      {duration}
                    </Typography>
                    {hasTwoLineDuration && (
                      <Typography
                        variant="slotDuration"
                        color="text.secondary"
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%',
                        }}
                      >
                        min
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
              {/* +/- buttons */}
              <Stack direction="row" p={0.5} spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                {/* Decrement button (-) */}
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleLongPressStart(-1);
                  }}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  onPointerDown={(e) => {
                    if (isTouchDevice) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleLongPressStart(-1);
                    }
                  }}
                  onPointerUp={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressEnd();
                    }
                  }}
                  onPointerCancel={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressEnd();
                    }
                  }}
                  onPointerLeave={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressEnd();
                    }
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleLongPressStart(-1);
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    const wasLongPress = isLongPressingRef.current || !!longPressIntervalRef.current;
                    const touchDuration = Date.now() - touchStartTimeRef.current;
                    handleLongPressEnd();
                    if (!wasLongPress && touchDuration < 500) {
                      setTimeout(() => {
                        if (!longPressIntervalRef.current && !isLongPressingRef.current) {
                          const newTime = format(addMinutes(parse(durationAsTime, 'HH:mm', new Date()), -1), 'HH:mm');
                          handleDurationChange(newTime);
                        }
                      }, 10);
                    }
                  }}
                  onTouchCancel={(e) => {
                    e.stopPropagation();
                    handleLongPressEnd();
                  }}
                  onClick={(e) => {
                    if (isLongPressingRef.current || longPressIntervalRef.current) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    const newTime = format(addMinutes(parse(durationAsTime, 'HH:mm', new Date()), -1), 'HH:mm');
                    handleDurationChange(newTime);
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: '30px',
                    touchAction: 'manipulation',
                  }}
                >
                  <ArrowDropDownOutlined fontSize="large" />
                </Button>
                {/* Increment button (+) */}
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleLongPressStart(1);
                  }}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  onPointerDown={(e) => {
                    if (isTouchDevice) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleLongPressStart(1);
                    }
                  }}
                  onPointerUp={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressEnd();
                    }
                  }}
                  onPointerCancel={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressEnd();
                    }
                  }}
                  onPointerLeave={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressEnd();
                    }
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleLongPressStart(1);
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    const wasLongPress = isLongPressingRef.current || !!longPressIntervalRef.current;
                    const touchDuration = Date.now() - touchStartTimeRef.current;
                    handleLongPressEnd();
                    if (!wasLongPress && touchDuration < 500) {
                      setTimeout(() => {
                        if (!longPressIntervalRef.current && !isLongPressingRef.current) {
                          const newTime = format(addMinutes(parse(durationAsTime, 'HH:mm', new Date()), 1), 'HH:mm');
                          handleDurationChange(newTime);
                        }
                      }, 10);
                    }
                  }}
                  onTouchCancel={(e) => {
                    e.stopPropagation();
                    handleLongPressEnd();
                  }}
                  onClick={(e) => {
                    if (isLongPressingRef.current || longPressIntervalRef.current) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    const newTime = format(addMinutes(parse(durationAsTime, 'HH:mm', new Date()), 1), 'HH:mm');
                    handleDurationChange(newTime);
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: '30px',
                    touchAction: 'manipulation',
                  }}
                >
                  <ArrowDropUpOutlined fontSize="large" />
                </Button>
              </Stack>
            </Stack>
          </Stack>

          <Divider orientation="vertical" flexItem />

          {/* Main content area with form */}
          <Stack direction="row" alignItems="center" sx={{ p: 1, flex: 1, minWidth: 0 }}>
            <Box
              component="form"
              data-form-type="create"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(onSubmit)();
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 0.5,
                width: '100%',
                p: 0.5,
                height: '100%',
                minHeight: 0,
                justifyContent: 'center',
              }}
            >
              <Stack
                direction="column"
                spacing={1}
                sx={{ flex: 1, width: '100%', minWidth: 0, justifyContent: 'center' }}
              >
                {/* Show session type selector for sessions, or break display for breaks */}
                {openedAsBreakOnly ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      py: 0.5,
                      px: 0,
                      minHeight: '32px',
                    }}
                  >
                    <PauseCircleOutlined sx={{ fontSize: '1.25rem', color: 'text.secondary' }} />
                    <Typography component="span" color="text.primary" sx={{ fontSize: { xs: '16px', sm: '0.875rem' } }}>
                      {t('flows.detail.slotList.actions.break.title')}
                    </Typography>
                  </Box>
                ) : (
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => {
                      const { onChange: fieldTypeOnChange, ...fieldRest } = field;
                      return (
                        <FormControl
                          ref={selectFormControlRef}
                          variant="standard"
                          size="small"
                          error={!effectiveCreatingBreak && !!sessionErrors?.type}
                          sx={{ width: '100%' }}
                        >
                          <Select
                            {...fieldRest}
                            displayEmpty
                            open={selectOpen}
                            onChange={(e) => {
                              const v = e.target.value as string;
                              if (v === INLINE_BREAK_SELECT_VALUE) {
                                setValue('title', t('flows.detail.slotList.actions.break.title'), {
                                  shouldDirty: true,
                                  shouldValidate: false,
                                });
                              }
                              fieldTypeOnChange(e);
                              void trigger();
                            }}
                            onOpen={(e) => {
                              e?.stopPropagation();
                              isSelectInteractingRef.current = true;
                              setSelectOpen(true);
                              wasManuallyClosedRef.current = false;
                              // Reset the flag after a short delay to allow menu to open
                              setTimeout(() => {
                                isSelectInteractingRef.current = false;
                              }, 100);
                            }}
                            onClose={(e) => {
                              e?.stopPropagation();
                              // Delay resetting the flag to prevent click-away from firing immediately
                              setTimeout(() => {
                                isSelectInteractingRef.current = false;
                              }, 100);
                              setSelectOpenSafely(false);
                              wasManuallyClosedRef.current = true;
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              isSelectInteractingRef.current = true;
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              isSelectInteractingRef.current = true;
                            }}
                            disabled={createSessionInFlowPending || addingPending}
                            color="secondary"
                            renderValue={(value) => {
                              if (!value) {
                                return (
                                  <Typography component="span" color="text.primary">
                                    {t('session.form.selectType')}
                                  </Typography>
                                );
                              }
                              if (value === INLINE_BREAK_SELECT_VALUE) {
                                return (
                                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                                    <PauseCircleOutlined sx={{ fontSize: '1.25rem', color: 'text.secondary' }} />
                                    <Typography
                                      component="span"
                                      color="text.primary"
                                      sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                    >
                                      {t('flows.detail.slotList.actions.break.title')}
                                    </Typography>
                                  </Stack>
                                );
                              }
                              const sessionTypeValue = Object.values(SESSION_TYPE).find((type) => type === value);
                              if (!sessionTypeValue) return '';

                              const iconElement = sessionTypeToIcon(sessionTypeValue);
                              const title = t(`session.types.${sessionTypeValue}`);

                              return (
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                                  {isValidElement(iconElement)
                                    ? cloneElement(iconElement as ReactElement<{ sx?: { fontSize?: string } }>, {
                                        sx: { fontSize: '1.25rem' },
                                      })
                                    : iconElement}
                                  <Typography
                                    component="span"
                                    color="text.primary"
                                    sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                  >
                                    {title}
                                  </Typography>
                                </Stack>
                              );
                            }}
                            sx={{
                              'fontSize': '0.875rem',
                              '& .MuiSelect-select': {
                                py: 0,
                                px: 0,
                                fontSize: { xs: '16px', sm: '0.875rem' },
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                color: 'text.primary !important',
                              },
                              '& .MuiInputBase-root': {
                                height: '32px',
                                minHeight: '32px',
                                color: 'text.primary !important',
                                fontSize: { xs: '16px', sm: '0.875rem' },
                              },
                              '& .MuiInput-underline:after': {
                                borderBottomColor: 'secondary.main',
                              },
                              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                borderBottomColor: 'secondary.main',
                              },
                            }}
                            MenuProps={{
                              TransitionProps: {
                                onExiting: releaseSelectMenuFocus,
                              },
                              PaperProps: {
                                sx: {
                                  '& .MuiMenu-list': {
                                    py: 1,
                                  },
                                  '& .MuiMenuItem-root': {
                                    '&.Mui-selected': {
                                      'backgroundColor': 'action.selected',
                                      '&:hover': {
                                        backgroundColor: 'action.selected',
                                      },
                                      '& .MuiTypography-root': {
                                        color: 'secondary.main',
                                      },
                                    },
                                    '&:hover': {
                                      backgroundColor: 'action.hover',
                                    },
                                  },
                                  // Divider as `li`: no extra vertical gap vs between session rows
                                  '& li.MuiDivider-root': {
                                    my: 0,
                                    py: 0,
                                    minHeight: 0,
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value={INLINE_BREAK_SELECT_VALUE}>
                              <BreakSlotTypeInfo />
                            </MenuItem>
                            <Divider
                              component="li"
                              sx={{
                                my: 0,
                                py: 0,
                                minHeight: 0,
                                listStyle: 'none',
                              }}
                            />
                            {selectableSessionTypes.map((type) => (
                              <MenuItem key={type} value={type}>
                                <SessionTypeInfo type={type} />
                              </MenuItem>
                            ))}
                          </Select>
                          {!effectiveCreatingBreak && 'type' in errors && errors.type && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ fontSize: '0.75rem', mt: 0.25, display: 'none' }}
                            >
                              {errors.type.message}
                            </Typography>
                          )}
                        </FormControl>
                      );
                    }}
                  />
                )}
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      inputRef={titleInputRef}
                      fullWidth
                      variant="standard"
                      placeholder={
                        effectiveCreatingBreak
                          ? t('flows.detail.slotList.actions.break.form.titlePlaceholder')
                          : t('session.form.titleInputPlaceholder')
                      }
                      size="small"
                      error={!!errors.title}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmit(onSubmit)();
                        }
                      }}
                      sx={{
                        'fontSize': '0.875rem',
                        '& .MuiInputBase-root': {
                          fontSize: { xs: '16px', sm: '0.875rem' },
                          height: '32px',
                          minHeight: '32px',
                          color: 'text.primary !important',
                        },
                        '& .MuiInputBase-input': {
                          py: 0,
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          color: 'text.primary !important',
                          fontSize: { xs: '16px', sm: '0.875rem' },
                        },
                        '& .MuiInput-underline:before': {
                          borderBottomWidth: '1px',
                        },
                        '& .MuiInput-underline:after': {
                          borderBottomWidth: '1px',
                          borderBottomColor: 'secondary.main',
                        },
                      }}
                      disabled={createSessionInFlowPending || addingPending}
                    />
                  )}
                />
              </Stack>
            </Box>
          </Stack>

          <Divider orientation="vertical" flexItem />

          {/* ====================================================================
              RIGHT SECTION: Action Buttons (Confirm, Cancel)
              ==================================================================== */}
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
            sx={{
              minWidth: '65px',
              width: '65px',
              flexShrink: 0,
              p: 1,
              gap: 0.5,
            }}
          >
            <Button
              variant={isValid && !createSessionInFlowPending && !addingPending ? 'contained' : 'outlined'}
              color="secondary"
              size="small"
              onClick={() => {
                // Trigger form submission directly
                handleSubmit(onSubmit)();
              }}
              disabled={!isValid || createSessionInFlowPending || addingPending}
              sx={{
                'flex': 1,
                'minWidth': 50,
                'color': isValid && !createSessionInFlowPending && !addingPending ? undefined : 'secondary.main',
                '&:hover': {
                  backgroundColor:
                    isValid && !createSessionInFlowPending && !addingPending ? undefined : 'action.hover',
                },
                '&:disabled': {
                  color: 'action.disabled',
                },
              }}
            >
              <CheckCircleIcon fontSize="small" />
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleCancel}
              disabled={createSessionInFlowPending || addingPending}
              sx={{
                'flex': 1,
                'minWidth': 50,
                'color': 'action.active',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&:disabled': {
                  color: 'action.disabled',
                },
              }}
            >
              <CancelOutlinedIcon fontSize="small" />
            </Button>
          </Stack>
        </Stack>
      </Card>
    </ClickAwayListener>
  );
}
